/**
 * Expandable table rows — the shared disclosure primitive for `.data-table`.
 *
 * Extracted 2026-08-09, when Hotels and Customers gained the same expandable
 * detail row Agreements had. Three copies of this would have drifted: the aria
 * wiring and the zebra parity are both easy to get subtly wrong and impossible
 * to notice from the outside, because a broken copy still opens and closes.
 *
 * Two non-obvious rules are encoded here rather than left to each caller:
 *
 *  1. ZEBRA PARITY comes from the DATA index, never from `:nth-child`. An open
 *     row inserts a second `<tr>`, which flips the stripe of every row beneath
 *     it the moment you expand one. The detail row carries its parent's band so
 *     an open record reads as a single block. See the `[data-zebra]` rules in
 *     crm-styles.css.
 *  2. The detail row's `colSpan` must cover the data columns PLUS the leading
 *     disclosure column. Getting it wrong shears the table's right edge, and
 *     only when a row is open — so `totalColumns()` does the arithmetic once
 *     instead of each caller remembering the `+ 1`.
 *
 * Styling lives entirely in crm-styles.css (`.col-disclosure`, `.row-disclosure`,
 * `.detail-row`, `.detail-panel`), keyed on `.crm-scope .data-table` — so any
 * table inside the CRM shell gets the accessible 28px target and the measured
 * contrast (see disclosure-affordance.spec.ts) without opting in.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

/* ── The horizontal-scroll container ──────────────────────────────────────── */

/**
 * `.table-scroll`, plus the one measurement its detail panels need.
 *
 * Fixes Stacie's 2026-08-11 report that the hotel panel was "cut off" on the
 * right — data lost, not merely absent. The mechanism is worth understanding
 * before changing anything here:
 *
 * `.data-table` is `width: 100%` but grows past that on content, so this div
 * scrolls horizontally. A detail row's `<td>` spans the whole table, which makes
 * it as wide as the SCROLLED width rather than the visible width — so
 * `.detail-grid`'s `auto-fill` wraps against the wrong box and lays fields out
 * past the right edge of the viewport. They are reachable only by scrolling the
 * table sideways, which nothing on screen suggests.
 *
 * There is no CSS unit for "my scroll container's visible width", so the width is
 * measured here into `--detail-panel-width` and consumed by the sticky rule in
 * crm-styles.css. `contentBoxSize` rather than `clientWidth` on purpose: the phone
 * tier gives this container 14px of horizontal padding, and `clientWidth` includes
 * padding — using it would make every panel 28px too wide on a phone, which is
 * the same class of bug in miniature.
 *
 * Living in this file rather than each page means all three expandable tables
 * (Hotels, HSP Customers, Agreements) are fixed by construction, and a fourth
 * cannot be added with the bug.
 *
 * ── The SECOND measurement: where the box ends vertically (item 31) ─────────
 *
 * Stacie, 2026-08-19: "I have to scroll all the way to the bottom to then be able
 * to scroll to the right to see the Contract Rate and Last Booking columns."
 *
 * The container is already bounded — `max-height: max(320px, calc(100dvh - 240px))`
 * — and a bounded box puts its horizontal scrollbar at its own bottom edge, which
 * is the fix. The bug is the `240px`: it is a GUESS at how much page sits above the
 * table, and on Hotels the header, intro, period note, filter row and section
 * header together clear that, so the box's bottom edge — and with it the only
 * horizontal scrollbar — lands below the fold. You reach it by scrolling the page
 * to the end, which is exactly what she described.
 *
 * There is no CSS unit for "the distance from my top edge to the viewport bottom",
 * so it is measured here into `--table-scroll-max`, the same way the width is. The
 * CSS keeps the old expression as its fallback, so a failed or absent measurement
 * degrades to today's behaviour rather than to an unbounded table.
 */
/**
 * How tall the scroll box may be, given where its top edge sits.
 *
 * Pure, exported and unit-tested, because jsdom has no layout: the DOM wiring
 * below cannot be tested here, but the arithmetic that decides the number can be,
 * and the arithmetic is where an off-by-a-gutter hides.
 *
 * `floor` keeps the existing 320px minimum — on a short viewport a cramped-but-
 * scrollable table beats one squeezed to nothing. `gutter` leaves the box off the
 * very bottom edge of the viewport so the scrollbar is not flush against it.
 *
 * Returns null when the inputs cannot produce a trustworthy answer (a zero or
 * negative viewport, an unattached element measuring top 0) — the caller then
 * leaves the CSS fallback alone rather than writing a wrong bound.
 */
/**
 * FLOOR RAISED 320 → 520, which trades PART of item 31 back on purpose.
 *
 * Measured on the running app at 1440x900: the table's top edge sits 554px
 * down even after the header trim, so `viewportHeight - top - gutter` is 322px
 * and the box shows 3.9 rows of an 83px median row. Rows are two lines tall by
 * construction — the contact cell is name+email, the term cell is date plus
 * countdown — so shortening them is not available: widening the Hotel column
 * from 103px to 180px moved the median 83 → 79 and the row count 3.9 → 4.1.
 *
 * The arithmetic is therefore forced. Six rows of 83px is 498px, which on a
 * 900px viewport CANNOT fit under a 554px top edge. Jeff, 2026-09-09: "could we
 * make that a bit taller so you scroll down a bit more on the page before
 * scrolling the table? I know that the double scroll bar thing is way less than
 * ideal, but only being able to see [three] agreement rows at a time is also
 * less than ideal."
 *
 * WHAT THIS COSTS, stated plainly, because a test used to forbid it: when the
 * viewport cannot show the floor, the box's bottom edge — and the horizontal
 * scrollbar on it — sits below the fold, which is item 31's complaint. The
 * difference from the original bug is that item 31's bound was a WRONG GUESS
 * (a hard-coded 240px with no relationship to the content above), so the
 * overflow was arbitrary and unbounded. This is a floor chosen against a
 * measured row height: at 520px the overflow on a 900px viewport is ~174px —
 * "a bit more" page scroll, which is what was asked for.
 *
 * On any viewport tall enough, the measurement still wins and nothing hangs off
 * the bottom.
 */
export function tableScrollMaxHeight(
  top: number,
  viewportHeight: number,
  gutter = 24,
  floor = 520,
): number | null {
  if (!Number.isFinite(top) || !Number.isFinite(viewportHeight)) return null;
  if (viewportHeight <= 0 || top <= 0) return null;
  return Math.max(floor, Math.round(viewportHeight - top - gutter));
}

export function TableScroll({
  children,
  style,
  testId,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  testId?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const apply = (px: number) => {
      // Guard against 0, which a display:none ancestor or an unattached first
      // measurement can produce. Setting width:0 would collapse the panel
      // entirely — a worse bug than the one being fixed, so leave the CSS
      // fallback in place instead.
      if (px > 0) el.style.setProperty('--detail-panel-width', `${px}px`);
    };

    // The vertical bound (item 31). Re-measured on viewport resize because the
    // top edge moves when the shell reflows; NOT on page scroll, because a
    // correctly bounded box is what stops the page scrolling in the first place,
    // and recomputing mid-scroll would make the box breathe as you read it.
    const applyHeight = () => {
      const px = tableScrollMaxHeight(
        el.getBoundingClientRect().top,
        window.innerHeight,
      );
      if (px != null) el.style.setProperty('--table-scroll-max', `${px}px`);
    };
    applyHeight();
    window.addEventListener('resize', applyHeight);

    // ResizeObserver is in every browser this app supports, but the guard keeps
    // jsdom (which has no layout and, depending on version, no observer) from
    // throwing during render tests.
    if (typeof ResizeObserver === 'undefined') {
      apply(el.clientWidth);
      return () => window.removeEventListener('resize', applyHeight);
    }

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const box = entry.contentBoxSize?.[0];
        apply(box ? box.inlineSize : entry.contentRect.width);
      }
      // The table growing or shrinking can move everything below it, so the
      // vertical bound is refreshed on the same signal as the width.
      applyHeight();
    });
    ro.observe(el);
    return () => {
      window.removeEventListener('resize', applyHeight);
      ro.disconnect();
    };
  }, []);

  return (
    <div className="table-scroll" data-testid={testId} ref={ref} style={style}>
      {children}
    </div>
  );
}

/* ── State ────────────────────────────────────────────────────────────────── */

export interface ExpandedRows {
  /** Is this row's detail panel open? */
  isExpanded: (id: string) => boolean;
  /** Open a closed row, close an open one. */
  toggle: (id: string) => void;
  /** Close everything. Call this when the underlying result set changes. */
  collapseAll: () => void;
  /** How many rows are currently open (for "collapse all" affordances). */
  expandedCount: number;
}

/**
 * Which rows are expanded.
 *
 * Component state, NOT the URL. Unlike a stage filter or a sort, "I opened
 * three rows to compare them" is transient reading posture — not a queue worth
 * bookmarking or sending to a colleague. A Set because several rows open at
 * once is the point: comparing two properties' GDS codes side by side is the
 * actual task.
 *
 * `collapseAll` is exposed rather than wired to a dependency array on purpose.
 * Callers clear it where they already handle a successful re-query, which keeps
 * the reset tied to "new rows arrived" instead of to a token changing — and
 * makes it obvious in the fetch code that expansion is being reset at all.
 * Leaving stale ids expanded would open whichever unrelated record happened to
 * reuse one.
 */
export function useExpandedRows(): ExpandedRows {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  return {
    isExpanded: (id: string) => expanded.has(id),
    toggle,
    collapseAll,
    expandedCount: expanded.size,
  };
}

/* ── Layout helpers ───────────────────────────────────────────────────────── */

/**
 * Data columns plus the leading disclosure column — the detail row's `colSpan`.
 * Trivial arithmetic, centralised because getting it wrong is invisible until a
 * row is open and then shears the table's right edge.
 */
export function totalColumns(dataColumns: number): number {
  return dataColumns + 1;
}

/** Stripe band for the row at this DATA index. See rule 1 in the file header. */
export function zebraFor(index: number): 'even' | 'odd' {
  return index % 2 === 0 ? 'even' : 'odd';
}

/**
 * Escape a value so it is safe as a single IDREF token.
 *
 * WHY THIS IS NOT COSMETIC. `aria-controls` is an IDREF **list**, tokenized on
 * ASCII whitespace. An id containing a space is therefore parsed by assistive
 * technology as TWO references — `book-detail-ae:owner:Sarah` and `Chen` —
 * neither of which exists, so the disclosure control resolves to nothing. The
 * page looks and behaves correctly for everyone else, which is why this survived
 * review once already.
 *
 * It also survived a test. `document.getElementById(attr)` does an EXACT string
 * match and happily finds an id containing a space, so a spec that resolves the
 * attribute that way passes while real AT fails. See the IDREF-tokenization test
 * in grouping.render.spec.tsx, which models the split instead.
 *
 * ESCAPING, NOT STRIPPING, because these ids are also `aria-controls` targets and
 * React keys: two different inputs must never produce the same output. Stripping
 * or replacing whitespace with `-` would map the AE "Sarah Chen" and an AE
 * literally named "Sarah-Chen" onto one id, wiring one group's button to another
 * group's rows — the same collision class as the `unassigned` sentinel.
 *
 * `_<hex>_` is delimited on BOTH sides on purpose. With a bare `_<hex>` prefix the
 * encoding is ambiguous: `_` (0x5f) and the two characters 0x5 + `f` both produce
 * `_5f`. The trailing delimiter makes it injective — `_5f_` versus `_5_f`.
 *
 * `:` is deliberately allowed: HTML5 permits it in an id, `getElementById`
 * handles it, and the grouping keys are built from it (`ae:owner:…`), so keeping
 * it leaves the ids readable in the DOM inspector.
 */
export function idrefSafe(raw: string): string {
  return raw.replace(
    /[^A-Za-z0-9.:-]/g,
    (c) => `_${c.codePointAt(0)!.toString(16)}_`,
  );
}

/**
 * Stable DOM id for a row's detail panel, so `aria-controls` can point at it.
 *
 * Escapes the id rather than trusting the caller. Three of the four call sites
 * pass a database id and were always safe; the fourth passes a grouping key built
 * from a person's full name. Sanitising HERE rather than at that one call site is
 * what stops the next caller reintroducing it.
 */
export function detailRowId(prefix: string, id: string): string {
  return `${prefix}-detail-${idrefSafe(id)}`;
}

/* ── Cells ────────────────────────────────────────────────────────────────── */

/**
 * The header above the disclosure column. Visually empty but accessibly named,
 * so screen readers announce a column rather than reading an unlabelled cell.
 */
export function DisclosureHeaderCell() {
  return (
    <th className="col-disclosure">
      <span className="sr-only">Expand row</span>
    </th>
  );
}

/**
 * The disclosure button cell.
 *
 * `label` names the RECORD, not the control — it becomes "Show detail for
 * Hampton Inn Dallas". A bare "Expand" repeated down a fifty-row table tells a
 * screen-reader user nothing about which row they are on.
 */
export function DisclosureCell({
  controls,
  label,
  onToggle,
  open,
  testId,
}: {
  /** DOM id of the detail row this button opens (see `detailRowId`). */
  controls: string;
  /** Human name of the record, e.g. the hotel or customer name. */
  label: string;
  onToggle: () => void;
  open: boolean;
  testId?: string;
}) {
  return (
    <td className="col-disclosure">
      <button
        aria-controls={controls}
        aria-expanded={open}
        aria-label={`${open ? 'Hide' : 'Show'} detail for ${label}`}
        className="row-disclosure"
        data-testid={testId}
        onClick={onToggle}
        type="button"
      >
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
    </td>
  );
}

/**
 * The full-width row holding a detail panel.
 *
 * Takes `zebra` from its parent rather than computing its own, which is what
 * makes an open record read as one block instead of two alternating stripes.
 */
export function DetailRow({
  children,
  colSpan,
  id,
  testId,
  zebra,
}: {
  children: React.ReactNode;
  colSpan: number;
  id: string;
  testId?: string;
  zebra: 'even' | 'odd';
}) {
  return (
    <tr className="detail-row" data-testid={testId} data-zebra={zebra} id={id}>
      <td colSpan={colSpan}>{children}</td>
    </tr>
  );
}
